import { debounce, difference } from 'lodash-es';
import { GlCollapsibleListbox, GlButton, GlAvatar, GlIcon, GlBadge, GlSprintf } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import fuzzaldrinPlus from 'fuzzaldrin-plus';
import { __ } from '~/locale';
import { userIsDisabled, userDisabledAttributes } from '~/ai/agents_utils';
import { FLOW_TRIGGER_EVENTS } from '~/vue_shared/constants';
import { InternalEvents } from '../../../tracking/index.js?vue3';
import { DEFAULT_DEBOUNCE_AND_THROTTLE_MS } from '~/lib/utils/constants';
import { uuids } from '~/lib/utils/uuids';
import { TYPENAME_MERGE_REQUEST } from '~/graphql_shared/constants';
import { convertToGraphQLId } from '~/graphql_shared/utils';
import userAutocompleteWithMRPermissionsQuery from 'ee_else_ce/graphql_shared/queries/project_autocomplete_users_with_mr_permissions.query.graphql';
import InviteMembersTrigger from '../../../invite_members/components/invite_members_trigger.vue?vue3';
import setReviewersMutation from '~/merge_requests/components/reviewers/queries/set_reviewers.mutation.graphql';
import { REQUEST_REVIEW_SIMPLE, SEARCH_SELECT_REVIEWER_EVENT, SELECT_REVIEWER_EVENT, REMOVE_REVIEWER_EVENT } from '../../constants';
import { getReviewersForList, suggestedPosition, setReviewersForList } from '../../utils/reviewer_positions';
import userPermissionsQuery from './queries/user_permissions.query.graphql';
function toUsernames(reviewers) {
    return reviewers.map((reviewer)=>reviewer.username);
}
export default {
    name: 'ReviewerDropdown',
    apollo: {
        userPermissions: {
            query: userPermissionsQuery,
            variables () {
                return {
                    fullPath: this.projectPath,
                    iid: this.issuableIid
                };
            },
            update: (data)=>data.project?.mergeRequest?.userPermissions || {}
        }
    },
    components: {
        GlCollapsibleListbox,
        GlButton,
        GlAvatar,
        GlIcon,
        GlBadge,
        GlSprintf,
        InviteMembersTrigger
    },
    mixins: [
        InternalEvents.mixin()
    ],
    inject: [
        'projectPath',
        'issuableId',
        'issuableIid',
        'directlyInviteMembers'
    ],
    props: {
        users: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        selectedReviewers: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        // Any user eligible to be a reviewer for this list (based on approval rule, etc.)
        eligibleReviewers: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        usage: {
            type: String,
            required: false,
            default: ()=>'complex'
        },
        uniqueId: {
            type: String,
            required: false,
            default: ()=>uuids()[0]
        },
        multipleSelectionEnabled: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data () {
        return {
            loading: false,
            search: '',
            searching: false,
            fetchedUsers: [],
            currentSelectedReviewers: toUsernames(this.selectedReviewers),
            userPermissions: {},
            cachedDisabledReviewers: []
        };
    },
    computed: {
        usersForList () {
            let users;
            if (this.fetchedUsers.length || this.search) {
                users = this.fetchedUsers;
            } else {
                users = this.users;
            }
            return users;
        },
        mappedUsers () {
            const items = [];
            const users = this.usersForList;
            if (this.visibleReviewers.length && !this.search) {
                items.push({
                    text: __('Reviewers'),
                    options: this.visibleReviewers.map((user)=>this.mapUser(user))
                });
            }
            const filteredUsers = users.filter((u)=>this.search ? true : !this.selectedReviewers.find(({ id })=>u.id === id)).map((user)=>{
                const mappedAttributes = this.mapUser(user);
                const disabledAttributes = userDisabledAttributes(user, FLOW_TRIGGER_EVENTS.ASSIGN_REVIEWER);
                return {
                    ...mappedAttributes,
                    ...disabledAttributes
                };
            });
            items.push({
                textSrOnly: true,
                text: __('Users'),
                options: this.moveCurrentUserToStart(filteredUsers)
            });
            return items;
        },
        visibleReviewers () {
            // Eligible users filtered to only show the previously selected users
            return this.selectedReviewers.filter((user)=>this.eligibleReviewers.map((gqlUser)=>gqlUser.id).includes(user.id));
        },
        selectedReviewersListboxModel: {
            get () {
                if (this.multipleSelectionEnabled) return this.currentSelectedReviewers;
                // We store this in an array format for compatibility reasons, but the
                // dropdown expects a string when on single item mode
                return this.currentSelectedReviewers[0] || '';
            },
            set (value) {
                this.currentSelectedReviewers = [].concat(value);
            }
        },
        unassignLabel () {
            return this.multipleSelectionEnabled ? __('Unassign all') : __('Unassign');
        },
        currentUser () {
            return {
                username: gon?.current_username,
                name: gon?.current_user_fullname,
                avatarUrl: gon?.current_user_avatar_url,
                mergeRequestInteraction: {
                    canMerge: Boolean(this.userPermissions?.canMerge)
                }
            };
        },
        isSearchEmpty () {
            return this.search === '';
        }
    },
    watch: {
        selectedReviewers (newVal) {
            this.currentSelectedReviewers = toUsernames(newVal);
            this.updateCachedDisabledReviewers();
        },
        usersForList () {
            this.updateCachedDisabledReviewers();
        }
    },
    created () {
        this.debouncedFetchAutocompleteUsers = debounce((search)=>this.fetchAutocompleteUsers(search), DEFAULT_DEBOUNCE_AND_THROTTLE_MS);
    },
    methods: {
        updateCachedDisabledReviewers () {
            const selectedReviewerIds = this.selectedReviewers.map((r)=>r.id);
            this.cachedDisabledReviewers = (this.usersForList || []).filter((u)=>userIsDisabled(u, FLOW_TRIGGER_EVENTS.ASSIGN_REVIEWER) && !selectedReviewerIds.includes(u.id)).map((u)=>u.username);
        },
        mapUser (user) {
            return {
                value: user.username,
                text: user.name,
                secondaryText: `@${user.username}`,
                ...user
            };
        },
        shownDropdown () {
            if (!this.users.length && !this.fetchedUsers.length) {
                this.fetchAutocompleteUsers();
            }
        },
        async fetchAutocompleteUsers (search = '') {
            if (this.fetchController) this.fetchController.abort();
            this.fetchController = new AbortController();
            this.search = search;
            this.searching = true;
            let users = [];
            if (this.users.length) {
                users = fuzzaldrinPlus.filter(// We're storing `name` and `username` as a combined string in
                // a new key `matcher` as fuzzaldrin-plus doesn't support searching
                // using multiple keys.
                this.users.map((u)=>({
                        ...u,
                        matcher: `${u.name} ${u.username}`
                    })), search, {
                    key: [
                        'matcher'
                    ]
                });
            } else {
                try {
                    const result = await this.$apollo.query({
                        query: userAutocompleteWithMRPermissionsQuery,
                        variables: {
                            search,
                            fullPath: this.projectPath,
                            mergeRequestId: convertToGraphQLId(TYPENAME_MERGE_REQUEST, this.issuableId)
                        },
                        context: {
                            fetchOptions: {
                                signal: this.fetchController.signal
                            },
                            queryDeduplication: false
                        }
                    });
                    users = result.data?.namespace?.users ?? [];
                } catch (error) {
                    if (error.name === 'AbortError') {
                        return;
                    }
                    throw error;
                }
            }
            if (!search) {
                const eligibleReviewers = toUsernames(users).filter((username)=>!this.cachedDisabledReviewers.includes(username));
                const unselectedEligibleReviewers = difference(eligibleReviewers, this.currentSelectedReviewers);
                setReviewersForList({
                    issuableId: this.issuableId,
                    listId: this.uniqueId,
                    reviewers: unselectedEligibleReviewers
                });
            }
            this.fetchedUsers = users;
            this.searching = false;
        },
        removeAllReviewers () {
            this.currentSelectedReviewers = [];
        },
        trackReviewersChangeEvent () {
            const telemetryEvent = this.search ? SEARCH_SELECT_REVIEWER_EVENT : SELECT_REVIEWER_EVENT;
            const previousUsernames = toUsernames(this.selectedReviewers);
            const listUsernames = toUsernames(this.usersForList);
            const suggested = getReviewersForList({
                issuableId: this.issuableId,
                listId: this.uniqueId
            });
            // Reviewers are always shown first if they are in the list,
            // so we should exclude them for when we check the position
            const selectableList = difference(listUsernames, previousUsernames);
            const additions = difference(this.currentSelectedReviewers, previousUsernames);
            const removals = difference(previousUsernames, this.currentSelectedReviewers);
            additions.forEach((added)=>{
                // Convert from 0- to 1-index
                const listPosition = selectableList.findIndex((user)=>user === added) + 1;
                const suggestedPos = suggestedPosition({
                    username: added,
                    list: suggested
                });
                this.trackEvent(telemetryEvent, {
                    value: listPosition,
                    suggested_position: suggestedPos,
                    selectable_reviewers_count: selectableList.length
                });
            });
            removals.forEach(()=>{
                this.trackEvent(REMOVE_REVIEWER_EVENT, {
                    via: `ui_${this.usage}`
                });
            });
            return additions.length;
        },
        processReviewers () {
            const additions = this.trackReviewersChangeEvent();
            this.updateReviewers();
            if (additions > 0 && this.usage === 'simple') {
                this.trackEvent(REQUEST_REVIEW_SIMPLE);
            }
        },
        async updateReviewers () {
            this.loading = true;
            // Filter out disabled reviewers from the selection
            const disabledUsernames = (this.usersForList || []).filter((u)=>userIsDisabled(u, FLOW_TRIGGER_EVENTS.ASSIGN_REVIEWER)).map((u)=>u.username);
            const filteredReviewers = this.currentSelectedReviewers.filter((username)=>!disabledUsernames.includes(username));
            await this.$apollo.mutate({
                mutation: setReviewersMutation,
                variables: {
                    reviewerUsernames: filteredReviewers,
                    projectPath: this.projectPath,
                    iid: this.issuableIid
                }
            });
            this.loading = false;
        },
        moveCurrentUserToStart (users = []) {
            const currentUsername = this.currentUser.username;
            const isSelected = this.selectedReviewers.some((reviewer)=>reviewer.username === currentUsername);
            if (!currentUsername || isSelected || !this.isSearchEmpty) return users;
            const currentUserInList = users.find((user)=>user.username === currentUsername);
            if (currentUserInList) {
                return [
                    currentUserInList,
                    ...users.filter((user)=>user.username !== currentUsername)
                ];
            }
            return [
                this.mapUser(this.currentUser),
                ...users
            ];
        },
        approvalRulesCount (reviewer) {
            return reviewer?.mergeRequestInteraction?.applicableApprovalRules?.length;
        },
        approvalRuleName (reviewer) {
            const [rule] = reviewer.mergeRequestInteraction.applicableApprovalRules;
            return rule.type.toLowerCase() === 'code_owner' ? __('Code Owner') : rule.name;
        }
    },
    i18n: {
        selectReviewer: __('Select reviewer')
    }
};
